import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomButton from "../Custom/CustomButton";
import GenricButton from "../Custom/GenericButton";
import {
  isNotNullOrEmptyArray,
  getButtonBackgroundColor,
} from "../../globals/utils";

const OrderDetailsDialog = ({ open, order, onClose, customActions }) => {
  const { t } = useTranslation();

  const filteredCustomActions = customActions?.filter(
    (action) => action.label !== "Make Payment"
  );

  if (!order) return null;

  const getStatusColor = (status) => {
    if (status === "DELIVERED") return "success";
    if (status === "CANCELLED") return "error";
    if (status === "PAID") return "success";
    return status === "DELIVERED" ? "success" : "warning";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const commonDetails = [
    { primary: "Order Type", secondary: order.order_type },
    {
      primary: "Requested Quantity",
      secondary: `${order.requested_quantity} kg`,
    },
    { primary: "Cost Per Kg", secondary: `Rs. ${order.cost_per_kg}` },
    {
      primary: "Delivery Method",
      secondary: order.is_self_pickup ? t("Self Pickup") : t("Delivery"),
    },
    { primary: "Delivery Charges", secondary: `Rs. ${order.delivery_charges}` },
    {
      primary: "Packaging Charges",
      secondary: `Rs. ${order.packaging_charges}`,
    },
    { primary: "Commission", secondary: `Rs. ${order.commission}` },
    { primary: "Total Amount", secondary: `Rs. ${order.total_amount_payable}` },
    { primary: "Payment Mode", secondary: order.payment_mode },
    {
      primary: "Status",
      secondary: (
        <Chip
          label={order.order_status}
          color={getStatusColor(order.order_status)}
          size="small"
        />
      ),
      isComponent: true,
    },
    { primary: "State", secondary: order.state_name },
    { primary: "District", secondary: order.district_name },
    { primary: "Post Office", secondary: order.postoffice_name },
    { primary: "Village", secondary: order.village },
    { primary: "Pincode", secondary: order.pincode },
    {
      primary: "Delivery Address",
      secondary: `${order.address_line1}, ${order.village}`,
    },
    { primary: "Created At", secondary: formatDate(order.created_at) },
  ];

  const farmerDetails = [
    { primary: "Farmer Name", secondary: order.farmer_name || "N/A" },
    { primary: "Farm Name", secondary: order.farm_name || "N/A" },
  ];

  const institutionDetails = [
    {
      primary: "Institution Name",
      secondary: order.institution_name || "N/A",
    },
    {
      primary: "Institution Phone",
      secondary: order.institution_phone_number || "N/A",
    },
  ];

  const detailItems =
    order.order_type === "FARMER"
      ? [...farmerDetails, ...commonDetails]
      : [...institutionDetails, ...commonDetails];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: "70vh",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          {t("Order Details")} #{order.id}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <List>
          {detailItems.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem>
                <ListItemText
                  primary={t(item.primary)}
                  secondary={
                    item.isComponent ? item.secondary : t(item.secondary)
                  }
                />
              </ListItem>
              {index < detailItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
      <DialogActions
        sx={{ pb: 2, px: 2, flexDirection: "column", alignItems: "stretch" }}
      >
        {isNotNullOrEmptyArray(filteredCustomActions) ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {filteredCustomActions.map((action, index) => (
              <GenricButton
                key={index}
                backgroundColor={getButtonBackgroundColor(action.color)}
                fontColor="#fff"
                fontSize="14px"
                padding="8px 15px"
                fullWidth={false}
                onClick={action.onClick}
                style={{ flexGrow: 1 }}
              >
                {t(action.label)}
              </GenricButton>
            ))}
            <CustomButton onClick={onClose} sx={{ flexGrow: 1 }}>
              {t("Close")}
            </CustomButton>
          </Box>
        ) : (
          <CustomButton onClick={onClose}>{t("Close")}</CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsDialog;
